// League waivers: your pending claims (reorder/cancel), a free-agent browser to
// submit claims, and a commissioner "Process Waivers" action.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { League, Team, Player, RosterSlot, WaiverClaim } from '../../types';
import BackLink from '../../components/common/BackLink';
import { seasonAverage } from '../../utils/playerSeason';
import ClaimPlayerModal from '../../components/league/ClaimPlayerModal';

const POSITION_MAX = 3;
const ROSTER_MAX = 6;

function PosBadge({ p }: { p: string }) {
  return <span className={`px-1.5 py-0.5 text-[0.65rem] font-display leading-none ${
    p === 'K' ? 'bg-ink-900 text-gold' : 'bg-success text-white'}`}>{p}</span>;
}

export default function WaiversPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();
  const id = Number(leagueId);

  const [league, setLeague] = useState<League | null>(null);
  const [myTeam, setMyTeam] = useState<Team | null>(null);
  const [myRoster, setMyRoster] = useState<RosterSlot[]>([]);
  const [claims, setClaims] = useState<WaiverClaim[]>([]);
  const [available, setAvailable] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimTarget, setClaimTarget] = useState<Player | null>(null);
  const [processResult, setProcessResult] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isCommissioner = league?.commissioner_id === user?.id;

  const refetchClaims = useCallback(async () => setClaims(await apiService.getMyWaiverClaims(id)), [id]);
  const refetchAvailable = useCallback(async () => setAvailable(await apiService.getWaiverAvailablePlayers(id)), [id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [lg, teams] = await Promise.all([apiService.getLeague(id), apiService.getTeams(id)]);
        setLeague(lg);
        const mine = teams.find((t) => t.user_id === user?.id) ?? null;
        setMyTeam(mine);
        if (mine) setMyRoster(await apiService.getRoster(mine.id));
        await Promise.all([refetchClaims(), refetchAvailable()]);
      } catch (e: any) {
        setPageError(e.message || 'Failed to load waivers');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user?.id, refetchClaims, refetchAvailable]);

  const posCounts = useMemo(() => {
    const c: Record<string, number> = { K: 0, P: 0 };
    myRoster.forEach((s) => { if (s.player) c[s.player.position] = (c[s.player.position] || 0) + 1; });
    return c;
  }, [myRoster]);

  const afterClaim = async () => { setClaimTarget(null); await Promise.all([refetchClaims(), refetchAvailable()]); };

  const cancel = async (claimId: number) => {
    setActionError(null);
    try { await apiService.cancelWaiverClaim(claimId); await refetchClaims(); }
    catch (e: any) { setActionError(e.message || 'Action failed'); }
  };
  const reorder = async (claimId: number, dir: 'up' | 'down') => {
    setActionError(null);
    try { await apiService.reorderWaiverClaim(claimId, dir); await refetchClaims(); }
    catch (e: any) { setActionError(e.message || 'Action failed'); }
  };
  const process = async () => {
    setProcessing(true);
    try {
      const r = await apiService.processWaivers(id);
      setProcessResult(`${r.processed} approved, ${r.denied} denied.`);
      await Promise.all([refetchClaims(), refetchAvailable(), myTeam && apiService.getRoster(myTeam.id).then(setMyRoster)]);
    } catch (e: any) {
      setProcessResult(e.message || 'Failed to process waivers');
    } finally { setProcessing(false); }
  };

  if (loading) return <p className="py-16 text-center text-secondary-500">Loading…</p>;
  if (pageError) {
    return (
      <div>
        <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>
        <p className="mt-6 text-sm text-danger">{pageError}</p>
      </div>
    );
  }

  return (
    <div>
      <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <div>
          <h1 className="font-display text-3xl uppercase tracking-tight text-ink-900">Waivers</h1>
          {league && <p className="text-sm text-secondary-500 mt-1">
            {league.name}{league.waivers_enabled ? '' : ' · Waivers mode off (instant add is on)'}</p>}
        </div>
        {isCommissioner && (
          <div className="text-right">
            <button onClick={process} disabled={processing}
              className="btn px-4 py-2 bg-ink-900 text-paper text-xs font-semibold uppercase tracking-wider hover:bg-ink-900/90 disabled:opacity-50">
              {processing ? 'Processing…' : 'Process Waivers Now'}
            </button>
            {processResult && <p className="text-xs text-secondary-500 mt-1">{processResult}</p>}
          </div>
        )}
      </div>

      {!myTeam ? (
        <p className="py-12 text-center text-sm text-secondary-500">You need a team in this league to make waiver claims.</p>
      ) : (
        <>
          <section data-testid="my-claims" className="mb-8">
            {actionError && <p className="text-sm text-danger mb-3">{actionError}</p>}
            <h2 className="font-display text-xl uppercase tracking-tight text-ink-900 border-b border-ink-900 pb-2 mb-4">My Claims</h2>
            {claims.filter((c) => c.status === 'pending').length === 0 ? (
              <p className="py-6 text-center font-display uppercase tracking-wider text-secondary-400">No pending claims.</p>
            ) : (
              <div className="bg-white border border-secondary-200 divide-y divide-secondary-200">
                {claims.filter((c) => c.status === 'pending').map((c, i, arr) => (
                  <div key={c.id} className="flex items-center gap-3 px-4 py-3">
                    <span className="font-display text-secondary-400 w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-ink-900">{c.player_add?.name}</span>
                      {c.player_add && <span className="ml-2"><PosBadge p={c.player_add.position} /></span>}
                      {c.player_drop && <span className="text-xs text-secondary-500 ml-2">drop {c.player_drop.name}</span>}
                    </div>
                    <button aria-label="Move up" disabled={i === 0} onClick={() => reorder(c.id, 'up')}
                      className="icon-btn p-1.5 text-secondary-400 hover:text-ink-900 disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
                    <button aria-label="Move down" disabled={i === arr.length - 1} onClick={() => reorder(c.id, 'down')}
                      className="icon-btn p-1.5 text-secondary-400 hover:text-ink-900 disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
                    <button aria-label="Cancel claim" onClick={() => cancel(c.id)}
                      className="icon-btn p-1.5 text-secondary-300 hover:text-danger"><TrashIcon className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section data-testid="available">
            <h2 className="font-display text-xl uppercase tracking-tight text-ink-900 border-b border-ink-900 pb-2 mb-4">Available Players</h2>
            <div className="bg-white border border-secondary-200 divide-y divide-secondary-200">
              {available.map((p) => {
                const season = seasonAverage(p);
                return (
                <div key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="text-sm font-medium text-ink-900 flex-1 min-w-0 truncate">{p.name}</span>
                  <PosBadge p={p.position} />
                  <span className="text-right leading-tight">
                    <span className="block font-display text-sm text-secondary-500 tabular-nums">{season.display}</span>
                    <span className="block text-[0.55rem] uppercase tracking-wider text-secondary-400">{season.label}</span>
                  </span>
                  <button onClick={() => setClaimTarget(p)}
                    className="btn px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper">Claim</button>
                </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {claimTarget && myTeam && (
        <ClaimPlayerModal
          leagueId={id} teamId={myTeam.id} addPlayer={claimTarget} myRoster={myRoster}
          rosterFull={myRoster.length >= ROSTER_MAX}
          positionFull={(posCounts[claimTarget.position] || 0) >= POSITION_MAX}
          onClose={() => setClaimTarget(null)} onSubmitted={afterClaim}
        />
      )}
    </div>
  );
}
